import { useMemo, useEffect } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { toTruncated } from '@/utilities/formatting';
import { format } from 'date-fns';
import TimeSeries, {
  Line,
  Area,
  DataTable,
  Title,
  Value,
} from '@/components/TimeSeries';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import track, { useTracking } from 'react-tracking';
import { useTranslation } from '@/hooks/useTranslation';

interface StaffProps {
  employees: number[][];
  employeesMin: number;
  employeesMax: number;
}

enum Series {
  series01 = 'employees',
}

const defaultFilter = {
  [Series.series01]: true,
};

const Staff = ({ employees, employeesMin, employeesMax }: StaffProps) => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const t = useTranslation();

  const { chart02Gradient01 } = useChartDefinitionsContext();

  const data = useMemo(
    () => ({
      [Series.series01]: employees,
    }),
    [employees]
  );

  const minMaxY = {
    [Series.series01]: [employeesMin, employeesMax],
  };

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data,
    defaultFilter,
  });

  useEffect(
    () =>
      dispatch({
        type: 'reset',
        payload: {
          data,
          defaultFilter,
        },
      }),
    [dispatch, data]
  );

  const colors = {
    [Series.series01]: theme.color.chart02,
  };

  const gradients = {
    [Series.series01]: {
      fillPositive: chart02Gradient01,
    },
  };

  const minimumY = Math.min(
    ...series.filter((n) => filter[n]).map((n) => minMaxY[n][0])
  );
  const maximumY = Math.max(
    ...series.filter((n) => filter[n]).map((n) => minMaxY[n][1])
  );

  function getTranslationFromKey(key: string) {
    if (key === Series.series01) {
      return t('page.companyReport.information.numberOfEmployees.chartLegend1');
    }
    return key;
  }
  const seriesWithLabel = series.reduce(
    (acc, current) => {
      return acc.concat([
        { key: current, label: getTranslationFromKey(current) },
      ]);
    },
    [] as { key: string; label: string }[]
  );

  return (
    <TimeSeries<Series>
      entities={entities}
      keys={keys}
      filter={filter}
      disabled={disabled}
      maximumX={maximumX}
      maximumY={maximumY}
      minimumX={minimumX}
      minimumY={minimumY > 0 ? 0 : minimumY}
      periods={periods}
      series={seriesWithLabel}
      seriesColorSelect={(series) => colors[series]}
      formatYAxis={(value) => toTruncated(value, 0)}
      onLegendClick={(series) => {
        trackEvent({
          legend: series,
          action: 'click',
          isVisible: !filter[series],
        });
        dispatch({
          type: 'filter',
          payload: {
            key: series,
            data,
          },
        });
      }}
      dataTable={({ key: date }) => {
        const entity = entities[date];
        return (
          <DataTable noWrap>
            <thead>
              <tr>
                <th colSpan={2}>
                  {date ? format(date, 'MMM dd yyyy') : `n/a`}
                </th>
              </tr>
            </thead>
            <tbody>
              {series
                .filter((n) => filter[n])
                .map((series) => {
                  const value =
                    entity !== undefined && entity[series] !== undefined
                      ? entity[series]
                      : undefined;
                  return (
                    <tr key={`data-table-row-${series}`}>
                      <Title
                        data-cy-id={`chart-staff-datatable-title-${series}`}
                      >
                        {getTranslationFromKey(series)}
                      </Title>
                      <Value
                        data-cy-id={`chart-staff-datatable-value-${series}`}
                        value={value}
                        colorPos={colors[series]}
                      >
                        {value !== undefined && toTruncated(value, 1)}
                      </Value>
                    </tr>
                  );
                })}
            </tbody>
          </DataTable>
        );
      }}
    >
      {({ xScale, yScale, keys, entities }) =>
        series
          .filter((n) => filter[n])
          .map((key) => {
            return (
              <g key={`chart-${key}`}>
                <Area
                  data={keys.filter((d) => entities[d][key] !== undefined)}
                  xSelect={(d) => d}
                  ySelect={(d) => entities[d][key]}
                  xScale={xScale}
                  yScale={yScale}
                  fillPos={gradients[key].fillPositive}
                  fillNeg={gradients[key].fillNegative}
                />
                <Line
                  data={keys.filter((d) => entities[d][key] !== undefined)}
                  xSelect={(d) => d}
                  ySelect={(d) => entities[d][key]}
                  xScale={xScale}
                  yScale={yScale}
                  strokePos={colors[key]}
                />
              </g>
            );
          })
      }
    </TimeSeries>
  );
};

export default track({
  type: 'track',
  subject: 'chart',
  modifier: 'Staff',
})(Staff) as typeof Staff;
